import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ApplyInsurancePageState, FindMemberPageState, InsuranceMemberStep } from '@typesApp/InsuranceMember'
import { BenefitsPageState } from '@typesApp/InsurancePlan'
import { ROUTES } from '@constants/index'
import { useActiveData } from '@hooks/useActiveData'
import { useAppNavigate } from '@hooks/useAppNavigate'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useDebouncedCallback } from '@hooks/useDebouncedCallback'
import { useErrorPage } from '@hooks/useErrorPage'
import { useFetchClaimRecord } from '@hooks/useFetchClaimRecord'
import { useInvokeInsuranceActionListeners } from '@hooks/useInvokeInsuranceActionListener'
import { usePathBuilder } from '@hooks/usePathBuilder'
import { useRestrictedCheck } from '@hooks/useRestrictedCheck'
import { useSessionInfo } from '@hooks/useSessionInfo'
import { clearLocalFormState } from '@utils/localFormState'
import { resetClaimRecordState } from '@utils/resetClaimRecordState'
import { insuranceMemberResumeController, InsuranceMemberResumeService } from './insuranceMemberResumeController'

export const useInsuranceMemberRedirect = (): void => {
  const dispatch = useDispatch()
  const buildPath = usePathBuilder()
  const navigate = useAppNavigate()
  const location = useLocation()
  const { showErrorPage } = useErrorPage()
  const invokeListeners = useInvokeInsuranceActionListeners()
  const { fetchActiveData } = useActiveData()
  const { claimRecordId } = useClaimRecord()
  const { fetchClaimRecord } = useFetchClaimRecord()
  const { fetchRestrictedStatus } = useRestrictedCheck()
  const { isTokenReady, customer, customerContext } = useSessionInfo()
  const isRedirectActiveRef = useRef(false)

  // insurer selection from navigation, e.g. from overview widget
  const navigateState = location.state as FindMemberPageState | undefined
  const insurerId = navigateState?.insurerId

  const controller = useMemo(() => {
    // callbacks for controller
    const service: InsuranceMemberResumeService = {
      fetchClaimRecord,
      fetchRestrictedStatus,

      onCustomerChangedError: async () => {
        await invokeListeners('remove')
        clearLocalFormState()
        resetClaimRecordState(dispatch)
      },

      onResourceNotFoundError: async () => {
        resetClaimRecordState(dispatch)
      },
    }
    return insuranceMemberResumeController(service)
  }, [dispatch, invokeListeners, fetchClaimRecord, fetchRestrictedStatus])

  const handler = useCallback(async () => {
    try {
      // claim record may have been created on another tab
      let latestClaimRecordId = claimRecordId
      if (!latestClaimRecordId && customer?.id) {
        const activeData = await fetchActiveData(customer.id)
        latestClaimRecordId = activeData?.id
      }

      const {
        step,
        customerAttemptsLeft: latestCustomerAttemptsLeft,
        claimRecord,
      } = await controller.initializeResumeState(latestClaimRecordId)

      if (!isRedirectActiveRef.current) {
        return
      }

      if (step === InsuranceMemberStep.FIND_MEMBER) {
        const state: FindMemberPageState = {
          insurerId,
          attemptsRemaining: latestCustomerAttemptsLeft,
        }

        navigate(buildPath(ROUTES.FIND_MEMBER), { state })
      } else if (step === InsuranceMemberStep.APPLY_INSURANCE) {
        const state: ApplyInsurancePageState = {
          claimRecordId: claimRecord?.id,
          claimRecordStatus: claimRecord?.status,
        }
        navigate(buildPath(ROUTES.APPLY_INSURANCE), { state })
      } else if (step === InsuranceMemberStep.MEMBER_FOUND) {
        const state: BenefitsPageState = {
          claimRecordId: claimRecord?.id,
          claimRecordStatus: claimRecord?.status,
          customerNumber: claimRecord?.customerNumber,
        }
        navigate(buildPath(ROUTES.BENEFITS), { state })
      } else if (step === InsuranceMemberStep.RESTRICTED) {
        navigate(buildPath(ROUTES.RESTRICTED))
      } else {
        showErrorPage()
      }
    } catch (error) {
      if (isRedirectActiveRef.current) {
        showErrorPage()
      }
    }
  }, [navigate, buildPath, showErrorPage, fetchActiveData, customer?.id, claimRecordId, insurerId, controller])

  const debouncedHandler = useDebouncedCallback(handler, 50)

  const shouldInit =
    isTokenReady ||
    customerContext?.customerType === 'ANONYMOUS' ||
    (customerContext?.customerType === 'GUEST' && !customer?.id)

  useEffect(() => {
    if (shouldInit) {
      isRedirectActiveRef.current = true
      debouncedHandler()

      return () => {
        isRedirectActiveRef.current = false
        debouncedHandler.cancel()
      }
    }
  }, [dispatch, debouncedHandler, shouldInit])
}
